/**
 * Terms of Service acceptance step.
 *
 * Rendered by ComplianceGate (which provides the page shell); this is the
 * card only. Scroll-to-bottom is enforced before the checkbox unlocks.
 *
 * FAIL-CLOSED: onAccept() fires only when the server confirms the acceptance
 * (res.ok). Any failure keeps the user on this step with an inline error —
 * the old behavior called onAccept() from the catch block ("let them through
 * anyway if API is down"), which made the gate decorative.
 */

import React, { useState, useRef, useEffect } from 'react';
import TermsContent, { TOS_VERSION } from '../../content/legal/TermsContent';

const TermsOfService = ({ userId, onAccept, getAuthToken }) => {
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    if (atBottom) setScrolledToBottom(true);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el && el.scrollHeight <= el.clientHeight) {
      setScrolledToBottom(true);
    }
  }, []);

  const handleAccept = async () => {
    if (!accepted || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const baseURL = import.meta.env.REACT_APP_API_URL || window.location.origin;
      const token = getAuthToken ? await getAuthToken() : null;
      const res = await fetch(`${baseURL}/api/v1/tos/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({ user_id: userId, tos_version: TOS_VERSION }),
      });
      if (res.ok) {
        onAccept();
        return;
      }
      setError("Couldn't record your acceptance. Nothing was saved — try again.");
    } catch (err) {
      console.error('Failed to record TOS acceptance:', err);
      setError("Couldn't record your acceptance. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        background: '#ffffff',
        border: '1px solid #e5e5e5',
        borderRadius: 14,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{ padding: '22px 28px 16px', borderBottom: '1px solid #f0f0f0' }}>
        <h1
          style={{
            fontFamily: '"Instrument Serif", Georgia, serif',
            fontWeight: 400, fontSize: 26, letterSpacing: '-0.02em',
            color: '#171717', margin: 0,
          }}
        >
          Terms of Service
        </h1>
        <p
          style={{
            margin: '4px 0 0',
            fontFamily: '"JetBrains Mono", ui-monospace, Menlo, monospace',
            fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase',
            color: '#a3a3a3',
          }}
        >
          Version {TOS_VERSION} · read to the end to continue
        </p>
      </div>

      {/* Terms content */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="prose prose-sm"
        style={{
          padding: '20px 28px',
          maxHeight: '48vh',
          overflowY: 'auto',
          fontSize: 13.5,
          lineHeight: 1.65,
          color: '#404040',
        }}
      >
        <TermsContent />
      </div>

      {/* Acceptance */}
      <div style={{ borderTop: '1px solid #f0f0f0', padding: '18px 28px 22px', background: '#fafafa' }}>
        {!scrolledToBottom && (
          <p style={{ margin: '0 0 12px', fontSize: 12.5, color: '#737373' }}>
            Scroll to the bottom of the Terms to continue.
          </p>
        )}

        <label
          style={{
            display: 'flex', gap: 12, alignItems: 'flex-start',
            opacity: scrolledToBottom ? 1 : 0.5,
            pointerEvents: scrolledToBottom ? 'auto' : 'none',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            disabled={!scrolledToBottom}
            style={{ marginTop: 3, width: 15, height: 15, accentColor: '#292524' }}
          />
          <span style={{ fontSize: 13.5, color: '#404040', lineHeight: 1.6 }}>
            I have read, understood, and agree to the{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#171717', fontWeight: 500 }}>Terms of Service</a>
            {' '}and{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#171717', fontWeight: 500 }}>Privacy Policy</a>,
            including the restrictions on duplication, reverse engineering, and competitive use.
          </span>
        </label>

        {error && (
          <p style={{ color: '#e11d48', fontSize: 13, margin: '12px 0 0', lineHeight: 1.5 }}>
            {error}
          </p>
        )}

        <button
          onClick={handleAccept}
          disabled={!accepted || submitting}
          style={{
            display: 'block', width: '100%', marginTop: 16,
            padding: '12px 16px', borderRadius: 10, border: 'none',
            fontFamily: '"Inter", system-ui, sans-serif',
            fontWeight: 500, fontSize: 14.5,
            cursor: accepted && !submitting ? 'pointer' : 'not-allowed',
            background: accepted && !submitting ? '#292524' : '#e5e5e5',
            color: accepted && !submitting ? '#ffffff' : '#a3a3a3',
            transition: 'background 150ms ease',
          }}
        >
          {submitting ? 'Recording acceptance…' : 'Accept and continue'}
        </button>
      </div>
    </div>
  );
};

export { TOS_VERSION };
export default TermsOfService;
